import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useOrg } from '../contexts/OrgContext';
import Header from '../components/Header';
import { formatType } from '../data/builtinTemplates';

const PAGE_SIZE = 20;

export default function Submissions() {
  const { membership } = useOrg();
  const [submissions, setSubmissions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [filterType, setFilterType] = useState('');
  const [filterAsset, setFilterAsset] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    if (!membership?.org_id) return;
    supabase
      .from('assets')
      .select('id, name, identifier')
      .eq('org_id', membership.org_id)
      .order('name')
      .then(({ data }) => { if (data) setAssets(data); });
  }, [membership?.org_id]);

  const fetchSubmissions = useCallback(async (pageNum = 0) => {
    if (!membership?.org_id) return;
    setLoading(true);

    let query = supabase
      .from('submissions')
      .select('id, checklist_type, overall_status, driver_name, created_at, vehicle_id, assets(name, identifier)', { count: 'exact' })
      .eq('org_id', membership.org_id)
      .order('created_at', { ascending: false })
      .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1);

    if (filterType) query = query.eq('checklist_type', filterType);
    if (filterAsset) query = query.eq('vehicle_id', filterAsset);
    if (filterStatus) query = query.eq('overall_status', filterStatus);
    if (filterFrom) query = query.gte('created_at', filterFrom);
    if (filterTo) query = query.lte('created_at', filterTo + 'T23:59:59');

    const { data, count, error } = await query;
    if (!error && data) {
      if (pageNum === 0) {
        setSubmissions(data);
      } else {
        setSubmissions((prev) => [...prev, ...data]);
      }
      setHasMore(count > (pageNum + 1) * PAGE_SIZE);
    }
    setLoading(false);
  }, [membership?.org_id, filterType, filterAsset, filterStatus, filterFrom, filterTo]);

  useEffect(() => {
    setPage(0);
    fetchSubmissions(0);
  }, [fetchSubmissions]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchSubmissions(next);
  };

  const clearFilters = () => {
    setFilterType('');
    setFilterAsset('');
    setFilterStatus('');
    setFilterFrom('');
    setFilterTo('');
  };

  const hasFilters = filterType || filterAsset || filterStatus || filterFrom || filterTo;

  return (
    <div className="min-h-screen bg-gray-900">
      <Header title="Submissions" backTo="/dashboard" />

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {/* Filters */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-gray-300 text-sm font-medium">Filters</h3>
            {hasFilters && (
              <button onClick={clearFilters} className="text-orange-400 text-xs hover:text-orange-300">
                Clear all
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All types</option>
              <option value="vehicle">Vehicle Pre-Start</option>
              <option value="pre_drill">Pre-Drill</option>
              <option value="drill_rig">Drill Rig Safety</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All statuses</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
            </select>

            <select
              value={filterAsset}
              onChange={(e) => setFilterAsset(e.target.value)}
              className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All assets</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.identifier ? ` (${a.identifier})` : ''}
                </option>
              ))}
            </select>

            <div className="col-span-2 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-gray-500 text-xs mb-1">From</label>
                <input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  className="w-full bg-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-gray-500 text-xs mb-1">To</label>
                <input
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  className="w-full bg-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading && submissions.length === 0 ? (
          <p className="text-gray-500 text-center py-10">Loading…</p>
        ) : submissions.length === 0 ? (
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
            <p className="text-gray-500">No submissions found</p>
          </div>
        ) : (
          <>
            <div className="space-y-2.5">
              {submissions.map((s) => (
                <Link
                  key={s.id}
                  to={`/submissions/${s.id}`}
                  className="block bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-600 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">
                      {formatType(s.checklist_type)}
                    </p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {s.driver_name}
                      {s.assets?.name && ` · ${s.assets.name}${s.assets.identifier ? ` (${s.assets.identifier})` : ''}`}
                    </p>
                    <p className="text-gray-600 text-xs mt-0.5">
                      {new Date(s.created_at).toLocaleString('en-AU')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="w-full bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-sm font-medium py-3 rounded-xl transition-colors disabled:opacity-50"
              >
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
